import {BrandHandle, DeliveryEstimates} from 'utilities/delivery-promise/types';
import {Nilable} from 'utilities/common';
import {ReportType} from 'utilities/error-logger/types';

import {
  STORE_PAGE_EVENT_MONORAIL_SCHEMA,
  FETCH_DELIVERY_PROMISE_EVENT_TYPE,
  BUY_WITH_PRIME_STORE_PAGE_EVENT_MONORAIL_SCHEMA,
  CLOSE_POSTAL_CODE_FORM,
  DELIVERY_PROMISE_ERROR,
  OPEN_POSTAL_CODE_FORM,
  UNABLE_TO_MOUNT,
  DELIVERY_PROMISE_FRONTEND_EVENT,
} from './consts';
import {
  produceMonorailEvent,
  deliveryPromiseResponseEventType,
  storePageEventPromisePayload,
  defaultMonorailEventPayload,
} from './helpers';
import {
  PromiseMonorailEvent,
  PromiseMonorailEventV2,
  ShopPromiseMonorailEvent,
} from './types';

interface TrackingOptions {
  productId?: number;
  variantId?: number;
  postalCodeOverride?: Nilable<string>;
  deliveryEstimate?: Nilable<DeliveryEstimates>;
  errorCode?: string;
}

function asNumber(value: Nilable<number | string>) {
  return parseInt(`${value}`, 10) || undefined;
}

export async function trackFetchingDeliveryPromise({
  productId,
  variantId,
  postalCodeOverride,
}: TrackingOptions) {
  const event: ShopPromiseMonorailEvent = {
    schemaId: STORE_PAGE_EVENT_MONORAIL_SCHEMA,
    payload: {
      ...(await defaultMonorailEventPayload()),
      eventType: FETCH_DELIVERY_PROMISE_EVENT_TYPE,
      productId: asNumber(productId),
      variantId: asNumber(variantId),
      isPostalCodeManuallyEntered: postalCodeOverride != null,
      isBrandShown: false,
      postalCode: postalCodeOverride ?? undefined,
    },
  };

  await produceMonorailEvent(event);
}

export async function trackDeliveryPromiseResponse(
  deliveryEstimate: Nilable<DeliveryEstimates>,
  postalCodeOverride: Nilable<string>,
) {
  if (!deliveryEstimate) {
    return;
  }

  const schemaId = schemaIdForDeliveryEstimate(deliveryEstimate);

  const event: PromiseMonorailEvent = {
    schemaId,
    payload: {
      ...(await defaultMonorailEventPayload()),
      eventType: deliveryPromiseResponseEventType(
        deliveryEstimate,
        postalCodeOverride,
      ),
      ...storePageEventPromisePayload(deliveryEstimate),
      productId: asNumber(deliveryEstimate.product),
      variantId: asNumber(deliveryEstimate.variant),
      isPostalCodeManuallyEntered: postalCodeOverride != null,
      postalCode: postalCodeOverride ?? deliveryEstimate.deliveryAddress.zip,
    },
  };

  await produceMonorailEvent(event);
}

export async function trackDeliveryPromiseLoaded() {
  const event: PromiseMonorailEventV2 = {
    schemaId: DELIVERY_PROMISE_FRONTEND_EVENT,
    payload: {
      ...(await defaultMonorailEventPayload()),
      requestId: '',
      eventType: 'loaded',
    },
  };

  await produceMonorailEvent(event);
}

export async function trackDeliveryPromiseFetching() {
  const event: PromiseMonorailEventV2 = {
    schemaId: DELIVERY_PROMISE_FRONTEND_EVENT,
    payload: {
      ...(await defaultMonorailEventPayload()),
      eventType: 'fetching',
      requestId: '',
    },
  };

  await produceMonorailEvent(event);
}

export async function trackDeliveryPromiseRenderEvent(
  requestId: string,
  noPromiseRenderedReason: string | undefined,
  renderingType: string,
) {
  const event: PromiseMonorailEventV2 = {
    schemaId: DELIVERY_PROMISE_FRONTEND_EVENT,
    payload: {
      ...(await defaultMonorailEventPayload()),
      requestId,
      noPromiseRenderedReason,
      renderingType,
      eventType: 'render',
    },
  };

  await produceMonorailEvent(event);
}

export async function trackDeliveryPromiseError(
  requestId: string,
  error: Error,
  message: string,
  severity: ReportType,
) {
  const schemaId = DELIVERY_PROMISE_FRONTEND_EVENT;

  const event: PromiseMonorailEventV2 = {
    schemaId,
    payload: {
      ...(await defaultMonorailEventPayload()),
      requestId,
      eventType: 'error',
      errorMessage: `Severity: ${severity.toString()} - ${error.name}: ${
        error.message
      } (${message})`,
    },
  };

  await produceMonorailEvent(event);
}

export async function trackDeliveryPromiseVisible(requestId: string) {
  const event: PromiseMonorailEventV2 = {
    schemaId: DELIVERY_PROMISE_FRONTEND_EVENT,
    payload: {
      ...(await defaultMonorailEventPayload()),
      requestId,
      eventType: 'visible',
    },
  };

  await produceMonorailEvent(event);
}

export async function trackFormToggle({
  productId,
  variantId,
  deliveryEstimate,
  postalCodeOverride,
  isOpen,
}: TrackingOptions & {isOpen: boolean}) {
  const schemaId = schemaIdForDeliveryEstimate(deliveryEstimate);

  const event: PromiseMonorailEvent = {
    schemaId,
    payload: {
      ...(await defaultMonorailEventPayload()),
      eventType: isOpen ? OPEN_POSTAL_CODE_FORM : CLOSE_POSTAL_CODE_FORM,
      ...storePageEventPromisePayload(deliveryEstimate),
      productId: asNumber(productId),
      variantId: asNumber(variantId),
      isPostalCodeManuallyEntered: postalCodeOverride != null,
      postalCode: postalCodeOverride ?? deliveryEstimate?.deliveryAddress?.zip,
    },
  };

  await produceMonorailEvent(event);
}

export async function trackError({
  variantId,
  productId,
  deliveryEstimate,
  postalCodeOverride,
  errorCode,
}: TrackingOptions) {
  const schemaId = schemaIdForDeliveryEstimate(deliveryEstimate);

  const event: PromiseMonorailEvent = {
    schemaId,
    payload: {
      ...(await defaultMonorailEventPayload()),
      eventType: DELIVERY_PROMISE_ERROR,
      ...storePageEventPromisePayload(deliveryEstimate),
      productId: asNumber(productId),
      variantId: asNumber(variantId),
      errorCode,
      isPostalCodeManuallyEntered: postalCodeOverride != null,
      postalCode: postalCodeOverride ?? deliveryEstimate?.deliveryAddress?.zip,
    },
  };

  await produceMonorailEvent(event);
}

export async function trackUnableToMount(themeName: Nilable<string>) {
  const event: PromiseMonorailEvent = {
    schemaId: STORE_PAGE_EVENT_MONORAIL_SCHEMA,
    payload: {
      ...(await defaultMonorailEventPayload()),
      eventType: UNABLE_TO_MOUNT,
      ...storePageEventPromisePayload(null),
      errorCode: `[Theme=${themeName}] is incompatible`,
    },
  };

  await produceMonorailEvent(event);
}

function schemaIdForDeliveryEstimate(
  deliveryEstimate: Nilable<DeliveryEstimates>,
): PromiseMonorailEvent['schemaId'] {
  if (
    deliveryEstimate?.selectedShippingOption?.brandedPromise?.handle ===
    BrandHandle.Prime
  ) {
    return BUY_WITH_PRIME_STORE_PAGE_EVENT_MONORAIL_SCHEMA;
  }

  return STORE_PAGE_EVENT_MONORAIL_SCHEMA;
}
